import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
	X,
	CircleCheck as CheckCircle2,
	Circle as XCircle,
	Package,
	ScanLine,
	TriangleAlert as AlertTriangle,
	Loader as Loader2,
	Printer,
	Store,
	ImageOff,
	List,
	MapPin,
	Image as ImageIcon,
	Keyboard,
} from 'lucide-react'
import { Button } from '../ui/Button'
import type { ReservationQueueItem, ReservationQueueLineItem } from '../../hooks/useReservationQueue'
import type { ProductBinLocation } from '../../types/warehouse'
import { getProductsBinLocations } from '../../lib/services/binInventoryService'
import { formatBinPath } from '../../utils/binLocationFormat'
import { resolveWedgeScan } from '../../utils/resolveWedgeScan'
import { classifyScanValue } from '../../utils/scanClassify'
import type {
	FulfillmentResult,
	FulfillReservationOptions,
	ReservationMissingItem,
} from '../../hooks/useReservations'
import type { ReservationStatus } from '../../types'
import { printMissingItems } from './missing-items-print'

type PickStatus = 'pending' | 'picked' | 'skipped'

interface PickRow extends ReservationQueueLineItem {
	status: PickStatus
}

interface ReservationPickModalProps {
	reservation: ReservationQueueItem
	adminId: string | null
	onClose: () => void
	onComplete: () => void
	fulfillReservation: (id: string, options?: FulfillReservationOptions) => Promise<FulfillmentResult>
	updateReservationStatus: (id: string, status: ReservationStatus, reason?: string) => Promise<boolean>
}

function itemLabel(item: ReservationQueueLineItem): string {
	const parts = [item.color, item.size].filter(Boolean)
	const suffix = parts.length ? ` (${parts.join(' / ')})` : ''
	return `${item.name || item.sku || 'Item'}${suffix}`
}

export const ReservationPickModal: React.FC<ReservationPickModalProps> = ({
	reservation,
	adminId,
	onClose,
	onComplete,
	fulfillReservation,
	updateReservationStatus,
}) => {
	const [rows, setRows] = useState<PickRow[]>(
		reservation.items.map((item) => ({ ...item, status: 'pending' }))
	)
	const [viewMode, setViewMode] = useState<'guided' | 'table'>('guided')
	const [currentIndex, setCurrentIndex] = useState(0)
	const [imageLoading, setImageLoading] = useState(true)
	const [scanInput, setScanInput] = useState('')
	const [scanFeedback, setScanFeedback] = useState<{ ok: boolean; message: string } | null>(null)
	const [processing, setProcessing] = useState(false)
	const [actionError, setActionError] = useState<string | null>(null)
	const [result, setResult] = useState<FulfillmentResult | null>(null)
	const [binLocations, setBinLocations] = useState<Map<string, ProductBinLocation>>(new Map())
	const [isManualEntry, setIsManualEntry] = useState(false)
	const inputRef = useRef<HTMLInputElement>(null)
	const scanBufferRef = useRef('')
	const scanTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
	const lastScanTimeRef = useRef(0)
	const lastScanValueRef = useRef('')
	const processScanRef = useRef<((raw: string) => Promise<void>) | null>(null)

	// The input is never focused on its own: on a tablet that opens the on-screen
	// keyboard over the pick information. Hardware scans arrive here instead.
	useEffect(() => {
		const handleKeyPress = (e: KeyboardEvent) => {
			const target = e.target
			if (
				target instanceof HTMLInputElement ||
				target instanceof HTMLTextAreaElement ||
				target instanceof HTMLSelectElement
			) {
				return
			}

			if (scanTimeoutRef.current) clearTimeout(scanTimeoutRef.current)
			const now = Date.now()

			if (e.key === 'Enter') {
				e.preventDefault()
				const buffer = scanBufferRef.current
				if (buffer.length === 0) return

				const dedupeMs = classifyScanValue(buffer) === 'epc' ? 1200 : 150
				const isDuplicate =
					now - lastScanTimeRef.current < dedupeMs && buffer === lastScanValueRef.current

				if (!isDuplicate) {
					lastScanTimeRef.current = now
					lastScanValueRef.current = buffer
					void processScanRef.current?.(buffer)
				}
				scanBufferRef.current = ''
				return
			}

			if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
				scanBufferRef.current += e.key
				scanTimeoutRef.current = setTimeout(() => {
					scanBufferRef.current = ''
				}, 300)
			}
		}

		window.addEventListener('keypress', handleKeyPress)
		return () => {
			window.removeEventListener('keypress', handleKeyPress)
			if (scanTimeoutRef.current) clearTimeout(scanTimeoutRef.current)
		}
	}, [])

	useEffect(() => {
		if (!adminId) return
		const productIds = Array.from(new Set(reservation.items.map((item) => item.product_id)))
		if (productIds.length === 0) return

		let cancelled = false
		void getProductsBinLocations(productIds, adminId).then((locations) => {
			if (!cancelled) setBinLocations(locations)
		})
		return () => {
			cancelled = true
		}
	}, [adminId, reservation.items])

	const binPathFor = (productId: string) => formatBinPath(binLocations.get(productId))

	const counts = useMemo(() => {
		const picked = rows.filter((r) => r.status === 'picked').length
		const skipped = rows.filter((r) => r.status === 'skipped').length
		const pending = rows.filter((r) => r.status === 'pending').length
		return { picked, skipped, pending }
	}, [rows])

	const currentRow = rows[currentIndex]

	const setStatus = (itemId: string, status: PickStatus) => {
		setRows((prev) => prev.map((r) => (r.item_id === itemId ? { ...r, status } : r)))
	}

	const goToIndex = (index: number) => {
		if (index < 0 || index >= rows.length) return
		setImageLoading(true)
		setScanFeedback(null)
		setCurrentIndex(index)
	}

	const advance = () => {
		// Move to the next still-pending item, else the immediate next, else stay.
		const nextPending = rows.findIndex((r, i) => i > currentIndex && r.status === 'pending')
		if (nextPending !== -1) goToIndex(nextPending)
		else if (currentIndex < rows.length - 1) goToIndex(currentIndex + 1)
	}

	const confirmCurrent = () => {
		if (!currentRow) return
		setStatus(currentRow.item_id, 'picked')
		setScanFeedback({ ok: true, message: `Picked ${itemLabel(currentRow)}` })
		advance()
	}

	const skipCurrent = () => {
		if (!currentRow) return
		setStatus(currentRow.item_id, 'skipped')
		advance()
	}

	const matchesValue = (row: PickRow, value: string) => {
		const normalized = value.trim().toLowerCase()
		if (!normalized) return false
		return (
			(row.sku != null && row.sku.toLowerCase() === normalized) ||
			(row.reference_code != null && row.reference_code.toLowerCase() === normalized)
		)
	}

	const matchesResolvedProduct = (row: PickRow, productId: string, sku?: string | null) => {
		if (row.product_id === productId) return true
		if (sku && row.sku && row.sku.toLowerCase() === sku.toLowerCase()) return true
		return false
	}

	const processScan = async (rawValue: string) => {
		const raw = rawValue.trim()
		if (!raw) return

		const result = await resolveWedgeScan(adminId, raw)
		setScanInput('')

		if (result.kind === 'unknown' || result.kind === 'epc_error') {
			setScanFeedback({ ok: false, message: result.message })
			return
		}

		if (result.kind === 'epc' && result.integrityAlarm) {
			setScanFeedback({
				ok: false,
				message: result.message || 'DATA INTEGRITY ALARM — unit record and EPC disagree',
			})
			return
		}

		const matchRow = (row: PickRow) => {
			if (result.kind === 'barcode') return matchesValue(row, result.value)
			return matchesResolvedProduct(row, result.item.id, result.item.sku)
		}

		const noUnitNote =
			result.kind === 'epc' && !result.unitFound
				? ' (no unit record — matched from EPC base)'
				: ''

		if (viewMode === 'guided') {
			if (currentRow && matchRow(currentRow)) {
				setStatus(currentRow.item_id, 'picked')
				setScanFeedback({
					ok: true,
					message: `Picked ${itemLabel(currentRow)}${noUnitNote}`,
				})
				advance()
			} else {
				const elsewhere = rows.find((r) => matchRow(r))
				setScanFeedback(
					elsewhere
						? { ok: false, message: `Wrong item - expected ${itemLabel(currentRow)}` }
						: { ok: false, message: `No item matches "${raw}"` }
				)
			}
			return
		}

		const match = rows.find((r) => matchRow(r))
		if (match) {
			setStatus(match.item_id, 'picked')
			setScanFeedback({
				ok: true,
				message: `Picked ${itemLabel(match)}${noUnitNote}`,
			})
		} else {
			setScanFeedback({ ok: false, message: `No item matches "${raw}"` })
		}
	}

	processScanRef.current = processScan

	const handleScan = async (event: React.FormEvent) => {
		event.preventDefault()
		await processScan(scanInput)
	}

	const getInitials = (value: string) =>
		value
			.split(' ')
			.map((word) => word[0])
			.join('')
			.substring(0, 2)
			.toUpperCase()

	const handleFulfill = async () => {
		setProcessing(true)
		setActionError(null)
		try {
			const pickedItems = rows.map((r) => ({
				item_id: r.item_id,
				picked_qty: r.status === 'skipped' ? 0 : r.quantity,
			}))

			const res = await fulfillReservation(reservation.id, { pickedItems })

			if (!res.success) {
				setActionError(res.error || 'Failed to fulfill reservation')
				return
			}

			setResult(res)

			const missing = res.missing ?? []
			if (missing.length > 0) {
				printMissingItems({
					reservationNumber: reservation.reservation_number,
					customerName: reservation.customer_name,
					missing,
				})
			}
		} catch (err) {
			setActionError(err instanceof Error ? err.message : 'Failed to fulfill reservation')
		} finally {
			setProcessing(false)
		}
	}

	const handleFinish = async () => {
		setProcessing(true)
		setActionError(null)
		try {
			const ok = await updateReservationStatus(reservation.id, 'ready')
			if (!ok) {
				setActionError('Failed to mark reservation ready')
				return
			}
			onComplete()
		} catch (err) {
			setActionError(err instanceof Error ? err.message : 'Failed to mark reservation ready')
		} finally {
			setProcessing(false)
		}
	}

	const renderResult = (res: FulfillmentResult) => {
		const missing = res.missing ?? []
		const fullySynced = !res.shopifySync || res.shopifySync.ok
		return (
			<div className="space-y-5">
				<div className="flex items-center gap-3">
					<div className="w-12 h-12 rounded-2xl bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
						<CheckCircle2 className="h-7 w-7 text-green-600 dark:text-green-400" />
					</div>
					<div>
						<h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">Reservation fulfilled</h3>
						<p className="text-sm text-gray-500 dark:text-gray-400">
							{reservation.is_store_pickup
								? 'Stock moved to the shop and the Shopify order was marked fulfilled.'
								: 'Stock moved to the shop and the reserved quantity was synced to Shopify.'}
						</p>
					</div>
				</div>

				{res.shopifySync?.attempted && (
					<div
						className={`p-3 rounded-xl text-sm flex items-start gap-2 ${
							fullySynced
								? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300'
								: 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300'
						}`}
					>
						{fullySynced ? (
							<CheckCircle2 className="h-4 w-4 mt-0.5 flex-shrink-0" />
						) : (
							<AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
						)}
						<span>
							{fullySynced
								? reservation.is_store_pickup
									? 'Shopify order fulfilled.'
									: 'Synced to Shopify.'
								: 'Shopify sync did not complete; it will retry automatically.'}
						</span>
					</div>
				)}

				{res.warning && (
					<div className="p-3 rounded-xl text-sm bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 flex items-start gap-2">
						<AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
						<span>{res.warning}</span>
					</div>
				)}

				{missing.length > 0 && (
					<div className="border border-red-200 dark:border-red-800 rounded-xl overflow-hidden">
						<div className="px-4 py-3 bg-red-50 dark:bg-red-900/20 flex items-center justify-between">
							<div className="flex items-center gap-2 text-red-700 dark:text-red-300 font-semibold text-sm">
								<AlertTriangle className="h-4 w-4" />
								{missing.length} item{missing.length === 1 ? '' : 's'} missing
							</div>
							<button
								onClick={() =>
									printMissingItems({
										reservationNumber: reservation.reservation_number,
										customerName: reservation.customer_name,
										missing,
									})
								}
								className="flex items-center gap-1.5 text-xs font-medium text-red-700 dark:text-red-300 hover:underline"
							>
								<Printer className="h-3.5 w-3.5" /> Reprint
							</button>
						</div>
						<div className="divide-y divide-red-100 dark:divide-red-900/40">
							{missing.map((m: ReservationMissingItem) => (
								<div key={m.item_id} className="px-4 py-3 text-sm">
									<div className="font-medium text-gray-900 dark:text-gray-100">
										{m.name || m.sku || 'Item'}
										{(m.color || m.size) && (
											<span className="text-gray-500 dark:text-gray-400">
												{' '}
												({[m.color, m.size].filter(Boolean).join(' / ')})
											</span>
										)}
									</div>
									<div className="text-gray-500 dark:text-gray-400 mt-0.5">
										{m.reference_code ? `Ref ${m.reference_code} · ` : ''}
										requested {m.requested}, fulfilled {m.transferred} ·{' '}
										<span className="text-red-600 dark:text-red-400 font-medium">
											{m.reason === 'insufficient_stock'
												? m.transferred > 0
													? 'partially out of stock'
													: 'out of stock'
												: 'skipped (not found)'}
										</span>
									</div>
								</div>
							))}
						</div>
					</div>
				)}

				<div className="flex justify-end">
					<Button onClick={onComplete}>Done</Button>
				</div>
			</div>
		)
	}

	return (
		<div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-2 sm:p-4 overflow-hidden">
			<div className="w-full max-w-2xl short:max-w-5xl bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-h-full flex flex-col">
				<div className="flex items-center justify-between p-5 short:px-4 short:py-3 border-b border-gray-100 dark:border-gray-700 shrink-0">
					<div className="flex items-center gap-3 min-w-0">
						<div className="w-11 h-11 rounded-xl bg-gradient-to-br from-cyan-500 to-teal-600 flex items-center justify-center flex-shrink-0">
							<Package className="h-6 w-6 text-white" />
						</div>
						<div className="min-w-0">
							<div className="flex items-center gap-2">
								<h2 className="text-lg font-bold text-gray-900 dark:text-gray-100 truncate">
									{reservation.reservation_number}
								</h2>
								{reservation.is_store_pickup && (
									<span className="px-2 py-0.5 rounded-full text-xs font-medium bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300 flex items-center gap-1">
										<Store className="h-3 w-3" /> Store pickup
									</span>
								)}
							</div>
							<p className="text-sm text-gray-500 dark:text-gray-400 truncate">
								{reservation.customer_name || 'Customer'}
								{reservation.source_order_number ? ` · ${reservation.source_order_number}` : ''}
							</p>
							{reservation.is_store_pickup && (reservation.customer_phone || reservation.customer_email) && (
								<p className="text-xs text-violet-600 dark:text-violet-300 truncate">
									{[reservation.customer_phone, reservation.customer_email].filter(Boolean).join(' · ')}
								</p>
							)}
						</div>
					</div>
					<button
						onClick={onClose}
						className="p-2 rounded-lg text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
					>
						<X className="h-5 w-5" />
					</button>
				</div>

				{result ? (
					<div className="flex-1 min-h-0 overflow-y-auto p-5">{renderResult(result)}</div>
				) : (
					<div className="flex-1 min-h-0 flex flex-col short:flex-row">
						<div className="flex-1 min-h-0 overflow-y-auto overscroll-contain p-5 short:p-4">
							<div className="flex items-center justify-center mb-4">
								<div className="inline-flex p-1 bg-gray-100 dark:bg-gray-700/50 rounded-xl">
									<button
										onClick={() => setViewMode('guided')}
										className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
											viewMode === 'guided'
												? 'bg-white dark:bg-gray-800 text-cyan-600 dark:text-cyan-400 shadow-sm'
												: 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
										}`}
									>
										<ImageIcon className="h-4 w-4" /> One by one
									</button>
									<button
										onClick={() => setViewMode('table')}
										className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
											viewMode === 'table'
												? 'bg-white dark:bg-gray-800 text-cyan-600 dark:text-cyan-400 shadow-sm'
												: 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
										}`}
									>
										<List className="h-4 w-4" /> Table
									</button>
								</div>
							</div>

							{viewMode === 'guided' ? (
								currentRow ? (
									<div className="space-y-4">
										<p className="text-center text-xs font-medium text-gray-400 dark:text-gray-500">
											Item {currentIndex + 1} of {rows.length}
										</p>

										<div
											className={`rounded-2xl border-2 p-5 short:p-3 transition-colors ${
												currentRow.status === 'picked'
													? 'border-green-300 dark:border-green-700 bg-green-50/60 dark:bg-green-900/20'
													: currentRow.status === 'skipped'
													? 'border-red-300 dark:border-red-700 bg-red-50/60 dark:bg-red-900/20'
													: 'border-gray-200 dark:border-gray-700'
											}`}
										>
											<div className="flex flex-col sm:flex-row short:flex-row items-center gap-5 short:gap-4">
												<div className="relative flex-shrink-0">
													{currentRow.image_url ? (
														<>
															{imageLoading && (
																<div className="absolute inset-0 w-40 h-40 short:w-28 short:h-28 bg-gray-100 dark:bg-gray-700 rounded-2xl animate-pulse flex items-center justify-center">
																	<Loader2 className="h-7 w-7 text-gray-400 animate-spin" />
																</div>
															)}
															<img
																src={currentRow.image_url}
																alt={currentRow.name || currentRow.sku || 'Item'}
																crossOrigin="anonymous"
																onLoad={() => setImageLoading(false)}
																onError={() => setImageLoading(false)}
																className={`w-40 h-40 short:w-28 short:h-28 object-contain rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 transition-opacity duration-200 ${
																	imageLoading ? 'opacity-0' : 'opacity-100'
																}`}
															/>
														</>
													) : (
														<div className="w-40 h-40 short:w-28 short:h-28 rounded-2xl flex flex-col items-center justify-center border border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-700">
															<ImageOff className="h-10 w-10 text-gray-400 mb-1" />
															<span className="text-2xl font-bold text-gray-400">
																{getInitials(currentRow.name || currentRow.sku || 'Item')}
															</span>
														</div>
													)}
												</div>

												<div className="flex-1 min-w-0 text-center sm:text-left short:text-left">
													<h3 className="text-xl short:text-lg font-bold text-gray-900 dark:text-gray-100 leading-tight">
														{currentRow.name || currentRow.sku || 'Item'}
													</h3>
													{currentRow.sku && (
														<p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
															SKU: <span className="font-mono font-semibold text-cyan-600 dark:text-cyan-400">{currentRow.sku}</span>
														</p>
													)}
													{currentRow.reference_code && (
														<p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
															Ref {currentRow.reference_code}
														</p>
													)}

													{(currentRow.color || currentRow.size) && (
														<div className="flex flex-wrap items-center justify-center sm:justify-start gap-2 mt-3">
															{currentRow.color && (
																<span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-pink-50 dark:bg-pink-900/30 border border-pink-200 dark:border-pink-800 text-pink-700 dark:text-pink-300 text-sm">
																	<span className="text-[10px] uppercase tracking-wider opacity-70 font-semibold">Color</span>
																	<span className="font-bold">{currentRow.color}</span>
																</span>
															)}
															{currentRow.size && (
																<span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-300 text-sm">
																	<span className="text-[10px] uppercase tracking-wider opacity-70 font-semibold">Size</span>
																	<span className="font-bold">{currentRow.size}</span>
																</span>
															)}
														</div>
													)}

													{binPathFor(currentRow.product_id) && (
														<div className="flex flex-wrap items-center justify-center sm:justify-start gap-2 mt-3">
															<span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-emerald-50 dark:bg-emerald-900/30 border border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-300 text-sm">
																<MapPin className="h-4 w-4" />
																<span className="text-[10px] uppercase tracking-wider opacity-70 font-semibold">Bin</span>
																<span className="font-bold">{binPathFor(currentRow.product_id)}</span>
															</span>
														</div>
													)}

													<div className="flex flex-wrap items-center justify-center sm:justify-start gap-3 mt-4">
														<div className="flex items-baseline gap-1.5 px-4 py-2 bg-cyan-50 dark:bg-cyan-900/20 rounded-xl border border-cyan-200 dark:border-cyan-800">
															<span className="text-3xl font-black text-cyan-600 dark:text-cyan-400">
																{currentRow.quantity}
															</span>
															<span className="text-sm text-cyan-700 dark:text-cyan-300 font-medium">units</span>
														</div>
														{currentRow.available < currentRow.quantity && (
															<span className="px-3 py-2 rounded-xl bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 text-sm font-medium">
																only {currentRow.available} in stock
															</span>
														)}
													</div>
												</div>
											</div>
										</div>

										<div className="flex gap-3">
											<Button
												variant="secondary"
												onClick={skipCurrent}
												className="flex-1"
											>
												<XCircle className="h-5 w-5 mr-2" /> Skip (missing)
											</Button>
											<Button
												onClick={confirmCurrent}
												className="flex-1 bg-gradient-to-r from-cyan-500 to-teal-600 hover:from-cyan-600 hover:to-teal-700"
											>
												<CheckCircle2 className="h-5 w-5 mr-2" /> Confirm pick
											</Button>
										</div>

										<div className="flex flex-wrap gap-1.5 justify-center pt-1">
											{rows.map((row, idx) => (
												<button
													key={row.item_id}
													onClick={() => goToIndex(idx)}
													title={itemLabel(row)}
													className={`w-9 h-9 rounded-lg flex items-center justify-center text-xs font-bold transition-all ${
														idx === currentIndex
															? 'ring-2 ring-cyan-400 ' +
															  (row.status === 'picked'
																	? 'bg-green-500 text-white'
																	: row.status === 'skipped'
																	? 'bg-red-500 text-white'
																	: 'bg-cyan-500 text-white')
															: row.status === 'picked'
															? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400'
															: row.status === 'skipped'
															? 'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400'
															: 'bg-gray-100 dark:bg-gray-700 text-gray-500 hover:bg-gray-200 dark:hover:bg-gray-600'
													}`}
												>
													{row.status === 'picked' ? (
														<CheckCircle2 className="h-4 w-4" />
													) : row.status === 'skipped' ? (
														<XCircle className="h-4 w-4" />
													) : (
														idx + 1
													)}
												</button>
											))}
										</div>
									</div>
								) : (
									<p className="text-center text-sm text-gray-500 dark:text-gray-400 py-8">
										This reservation has no items to pick.
									</p>
								)
							) : (
								<div className="space-y-2">
									{rows.map((row) => (
										<div
											key={row.item_id}
											className={`p-3 rounded-xl border flex items-center gap-3 transition-colors ${
												row.status === 'picked'
													? 'border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20'
													: row.status === 'skipped'
													? 'border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20'
													: 'border-gray-200 dark:border-gray-700'
											}`}
										>
											<div className="w-12 h-12 rounded-lg flex-shrink-0 overflow-hidden border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 flex items-center justify-center">
												{row.image_url ? (
													<img
														src={row.image_url}
														alt={row.name || row.sku || 'Item'}
														crossOrigin="anonymous"
														className="w-full h-full object-contain"
													/>
												) : (
													<ImageOff className="h-5 w-5 text-gray-400" />
												)}
											</div>
											<div className="flex-1 min-w-0">
												<p className="font-medium text-gray-900 dark:text-gray-100 truncate">
													{itemLabel(row)}
												</p>
												<p className="text-xs text-gray-500 dark:text-gray-400">
													{row.sku ? `SKU ${row.sku} · ` : ''}
													Qty {row.quantity}
													{row.available < row.quantity && (
														<span className="text-amber-600 dark:text-amber-400">
															{' '}
															· only {row.available} in stock
														</span>
													)}
												</p>
												{binPathFor(row.product_id) && (
													<p className="text-xs text-emerald-600 dark:text-emerald-400 flex items-center gap-1 mt-0.5">
														<MapPin className="h-3 w-3 flex-shrink-0" />
														<span className="truncate">{binPathFor(row.product_id)}</span>
													</p>
												)}
											</div>
											<div className="flex items-center gap-2 flex-shrink-0">
												<button
													onClick={() => setStatus(row.item_id, 'picked')}
													className={`p-2 rounded-lg transition-colors ${
														row.status === 'picked'
															? 'bg-green-600 text-white'
															: 'bg-gray-100 dark:bg-gray-700 text-gray-500 hover:bg-green-100 dark:hover:bg-green-900/30'
													}`}
													title="Picked"
												>
													<CheckCircle2 className="h-5 w-5" />
												</button>
												<button
													onClick={() => setStatus(row.item_id, 'skipped')}
													className={`p-2 rounded-lg transition-colors ${
														row.status === 'skipped'
															? 'bg-red-600 text-white'
															: 'bg-gray-100 dark:bg-gray-700 text-gray-500 hover:bg-red-100 dark:hover:bg-red-900/30'
													}`}
													title="Skip (missing)"
												>
													<XCircle className="h-5 w-5" />
												</button>
											</div>
										</div>
									))}
								</div>
							)}
						</div>

						<div className="shrink-0 short:w-[22rem] short:overflow-y-auto short:overscroll-contain border-t short:border-t-0 short:border-l border-gray-100 dark:border-gray-700 p-5 short:p-4 space-y-3 pb-keyboard">
							<form onSubmit={handleScan}>
								<div className="relative">
									<ScanLine className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
									<input
										ref={inputRef}
										type="text"
										value={scanInput}
										onChange={(e) => setScanInput(e.target.value)}
										placeholder={
											isManualEntry
												? 'Type SKU or reference...'
												: 'Scan barcode or UHF tag...'
										}
										inputMode={isManualEntry ? 'text' : 'none'}
										enterKeyHint="done"
										autoComplete="off"
										autoCorrect="off"
										spellCheck={false}
										className="w-full pl-12 pr-14 py-3 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-gray-100 placeholder-gray-500 focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
									/>
									<button
										type="button"
										onClick={() => {
											const next = !isManualEntry
											setIsManualEntry(next)
											if (next) inputRef.current?.focus()
											else inputRef.current?.blur()
										}}
										className={`absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-lg transition-colors ${
											isManualEntry
												? 'bg-cyan-100 dark:bg-cyan-900/40 text-cyan-600 dark:text-cyan-400'
												: 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-200'
										}`}
										title={
											isManualEntry
												? 'Manual entry on - tap to return to scanner only'
												: 'Type manually'
										}
									>
										<Keyboard className="h-5 w-5" />
									</button>
								</div>
								{scanFeedback && (
									<p
										className={`mt-2 text-sm ${
											scanFeedback.ok
												? 'text-green-600 dark:text-green-400'
												: 'text-red-600 dark:text-red-400'
										}`}
									>
										{scanFeedback.message}
									</p>
								)}
							</form>

						{actionError && (
							<div className="p-3 rounded-xl bg-red-50 dark:bg-red-900/20 text-sm text-red-700 dark:text-red-300 flex items-center gap-2">
								<AlertTriangle className="h-4 w-4 flex-shrink-0" />
								{actionError}
							</div>
						)}
						<div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400">
							<span>
								{counts.picked} picked · {counts.skipped} skipped · {counts.pending} pending
							</span>
							{counts.pending > 0 && (
								<button
									onClick={() =>
										setRows((prev) =>
											prev.map((r) => (r.status === 'pending' ? { ...r, status: 'picked' } : r))
										)
									}
									className="text-cyan-600 dark:text-cyan-400 hover:underline font-medium"
								>
									Mark remaining picked
								</button>
							)}
						</div>
						{counts.pending > 0 && (
							<p className="text-xs text-amber-600 dark:text-amber-400">
								Mark each item as picked or skipped before fulfilling.
							</p>
						)}
						<div className="flex flex-col sm:flex-row gap-3">
							<Button
								variant="secondary"
								onClick={handleFinish}
								disabled={processing}
								className="flex-1"
							>
								{processing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
								Finish (Mark Ready)
							</Button>
							<Button
								onClick={handleFulfill}
								disabled={processing || counts.pending > 0}
								className="flex-1 bg-gradient-to-r from-cyan-500 to-teal-600 hover:from-cyan-600 hover:to-teal-700"
							>
								{processing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
								Fulfill Reservation
							</Button>
						</div>
						</div>
					</div>
				)}
			</div>
		</div>
	)
}
